import { StrictMode, Component, type PropsWithChildren, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { I18nextProvider } from 'react-i18next'
import { QueryClientProvider } from '@tanstack/react-query'
import {
  AuthProvider,
  createPersistedQueryClient,
  initDatadog,
} from '@wehunt/core-web'
import {
  i18n,
  appConfig,
  CacheIsolationEffect,
} from '@wehunt/clubhouse-web-viltrapport'
import '@wehunt/ui/styles/tokens.css'
import '@wehunt/ui/styles/components.css'
import './select-theme.css'
import { App } from './App'
import { AppleRepairNotice } from './AppleRepairNotice'

initDatadog({
  applicationId: appConfig.datadog?.applicationId,
  clientToken: appConfig.datadog?.clientToken,
  service: appConfig.datadog?.service ?? 'viltrapport-web',
  env: appConfig.datadog?.env,
  version: appConfig.datadog?.version,
})

const queryClient = createPersistedQueryClient({
  appKey: 'viltrapport-web',
  buster: '1',
  persistedKeys: ['authentication'],
})

function AppErrorFallback() {
  return (
    <div role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
      <h1>Något gick fel</h1>
      <p>Felet har rapporterats. Ladda om sidan eller försök igen senare.</p>
    </div>
  )
}

class ErrorBoundary extends Component<
  PropsWithChildren<{ fallback: ReactNode }>,
  { hasError: boolean }
> {
  state = { hasError: false }
  static getDerivedStateFromError() {
    return { hasError: true }
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary fallback={<AppErrorFallback />}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <CacheIsolationEffect />
            <App />
            <AppleRepairNotice />
          </AuthProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </ErrorBoundary>
  </StrictMode>,
)
